import { CSSProperties, memo } from 'react';
import { Carousel } from 'react-responsive-carousel';

import { useElementHeight } from '../../hooks';
import { DefaultComponentProps } from '../../types';
import { cn, isNotNullish } from '../../utils';
import Wizard, { WizardBackButton } from '../Wizard';

import {
  StepFormCarouselVariableNames,
  DEFAULT_FOOTER_HEIGHT_PX,
  StepFormCarouselClassNames,
} from './constants';
import {
  StepComponentWrapper,
  StepFormCarouselFooter,
  StepFormCarouselRoot,
} from './StepFormCarousel.styled';
import type { StepComponent } from './types';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

export interface StepFormProps extends DefaultComponentProps {
  stepComponents: StepComponent[];
  /** current step index */
  currentStep: number;
  /** index of completed steps */
  completedSteps: number[];
  setCurrentStep: (step: number) => void;
}

const StepFormCarousel = memo(
  ({
    stepComponents: steps,
    currentStep: activeStepIndex,
    completedSteps: completedStepsIndexes,
    setCurrentStep: onStepIndexChange,
    className,
    'data-testid': dataTestId,
  }: StepFormProps) => {
    const {
      height: footerHeight = DEFAULT_FOOTER_HEIGHT_PX,
      elementRef: footerRef,
    } = useElementHeight();

    const handleBackButtonClick = () => {
      if (activeStepIndex === 0) return;

      onStepIndexChange(activeStepIndex - 1);
    };

    const handleStepClick = (stepId: string) => {
      onStepIndexChange(steps.findIndex(item => item.id === stepId));
    };

    const completedStepsIds = completedStepsIndexes
      .map(completedIndex => steps[completedIndex]?.id)
      .filter(isNotNullish);

    const isBackButtonHidden = activeStepIndex === 0;

    const activeStep = steps[activeStepIndex];

    return (
      <StepFormCarouselRoot
        className={cn(className, StepFormCarouselClassNames.Root)}
        data-testid={dataTestId}
        style={
          {
            [StepFormCarouselVariableNames.FooterHeight]: `${footerHeight}px`,
          } as CSSProperties
        }>
        <Carousel
          preventMovementUntilSwipeScrollTolerance
          selectedItem={activeStepIndex}
          showArrows={false}
          showIndicators={false}
          showStatus={false}
          showThumbs={false}>
          {steps.map(step => (
            <StepComponentWrapper
              className={StepFormCarouselClassNames.StepComponent}
              data-step-id={step.id}
              hidden={step.id !== activeStep?.id}
              key={step.title}>
              {step.component}
            </StepComponentWrapper>
          ))}
        </Carousel>

        <StepFormCarouselFooter
          className={StepFormCarouselClassNames.Footer}
          ref={footerRef}>
          <Wizard
            activeStepId={activeStep?.id}
            backButton={
              !isBackButtonHidden ? (
                <WizardBackButton onClick={handleBackButtonClick}>
                  {activeStep?.backText}
                </WizardBackButton>
              ) : undefined
            }
            completedStepIds={completedStepsIds}
            nextButton={activeStep?.nextButton}
            steps={steps}
            onStepClick={handleStepClick}
          />
        </StepFormCarouselFooter>
      </StepFormCarouselRoot>
    );
  },
);

StepFormCarousel.displayName = 'StepFormCarousel';

export default StepFormCarousel;
